import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/api/client";
import { toast } from "sonner";
import { Search, X, Trash2, Eye, EyeOff, MessageSquare, CheckCircle2, XCircle, Filter, Star } from "lucide-react";

const STATUS_COLOR = {
  approved: "bg-green-100 text-green-700",
  pending: "bg-amber-100 text-amber-700",
  rejected: "bg-red-100 text-red-700",
  hidden: "bg-muted text-muted-foreground",
};

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [replyOn, setReplyOn] = useState(null);
  const [replyText, setReplyText] = useState("");

  const fetch = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("status", status);
      if (q) params.set("q", q);
      const { data } = await api.get(`/admin/reviews?${params.toString()}`);
      setReviews(data);
    } catch (e) {
      toast.error("Failed to load reviews");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [status]);

  const doStatus = async (r, s) => {
    try {
      await api.post(`/admin/reviews/${r.id}/status`, { status: s });
      toast.success(`Review ${s}`);
      fetch();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const doDelete = async (r) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try { await api.delete(`/admin/reviews/${r.id}`); toast.success("Deleted"); fetch(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"); }
  };

  const submitReply = async () => {
    if (!replyText.trim()) return;
    try {
      await api.post(`/admin/reviews/${replyOn.id}/reply`, { reply: replyText.trim() });
      toast.success("Reply saved");
      setReplyOn(null); setReplyText("");
      fetch();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const counts = useMemo(() => {
    const c = { total: reviews.length, approved: 0, pending: 0, rejected: 0, hidden: 0 };
    for (const r of reviews) { c[r.status] = (c[r.status] || 0) + 1; }
    return c;
  }, [reviews]);

  return (
    <div data-testid="admin-reviews">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {["approved", "pending", "rejected", "hidden"].map((s) => (
          <div key={s} className="card-fah p-3">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{s}</div>
            <div className={`mt-1 text-2xl font-bold ${STATUS_COLOR[s].split(" ").find(c => c.startsWith("text"))}`}>{counts[s] || 0}</div>
          </div>
        ))}
      </div>

      <div className="card-fah p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 bg-muted/40 border border-border rounded-lg px-3 py-2 flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && fetch()} placeholder="Search reviews (product, user, text)…" className="flex-1 text-sm focus:outline-none bg-transparent" data-testid="review-search" />
          {q && <button onClick={() => { setQ(""); fetch(); }}><X className="w-4 h-4 text-muted-foreground" /></button>}
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="text-sm px-3 py-2 border border-border rounded-lg bg-white" data-testid="review-status-filter">
          <option value="all">All Status</option>
          <option value="approved">Approved</option>
          <option value="pending">Pending</option>
          <option value="rejected">Rejected</option>
          <option value="hidden">Hidden</option>
        </select>
        <button onClick={fetch} className="bg-muted text-foreground text-sm font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5"><Filter className="w-3.5 h-3.5" /> Apply</button>
      </div>

      <div className="space-y-3" data-testid="admin-review-list">
        {loading && <div className="card-fah p-8 text-center text-muted-foreground">Loading…</div>}
        {!loading && reviews.length === 0 && <div className="card-fah p-8 text-center text-muted-foreground">No reviews in this view.</div>}
        {reviews.map((r) => (
          <div key={r.id} className="card-fah p-4" data-testid={`admin-review-${r.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-3 min-w-0 flex-1">
                {r.product_image && <img src={r.product_image} alt="" className="w-14 h-14 rounded object-cover shrink-0" />}
                <div className="min-w-0">
                  <div className="text-sm font-semibold line-clamp-1">{r.product_name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">by {r.user_name} · {new Date(r.created_at).toLocaleString("en-IN")}</div>
                  <div className="mt-1 flex items-center gap-2">
                    <div className="flex">{[1,2,3,4,5].map(n => <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "fill-secondary text-secondary" : "text-muted-foreground/40"}`} />)}</div>
                    {r.is_verified_purchase && <span className="text-[10px] font-bold uppercase text-green-700 bg-green-100 px-1.5 py-0.5 rounded">Verified</span>}
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${STATUS_COLOR[r.status]}`}>{r.status}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                {r.status !== "approved" && <IconBtn onClick={() => doStatus(r, "approved")} title="Approve" color="text-green-700" testid={`approve-${r.id}`}><CheckCircle2 className="w-4 h-4" /></IconBtn>}
                {r.status !== "rejected" && <IconBtn onClick={() => doStatus(r, "rejected")} title="Reject" color="text-red-700" testid={`reject-${r.id}`}><XCircle className="w-4 h-4" /></IconBtn>}
                {r.status !== "hidden" ? <IconBtn onClick={() => doStatus(r, "hidden")} title="Hide" color="text-amber-700" testid={`hide-${r.id}`}><EyeOff className="w-4 h-4" /></IconBtn>
                  : <IconBtn onClick={() => doStatus(r, "approved")} title="Unhide" color="text-primary" testid={`unhide-${r.id}`}><Eye className="w-4 h-4" /></IconBtn>}
                <IconBtn onClick={() => { setReplyOn(r); setReplyText(r.admin_reply || ""); }} title="Reply" color="text-primary" testid={`reply-btn-${r.id}`}><MessageSquare className="w-4 h-4" /></IconBtn>
                <IconBtn onClick={() => doDelete(r)} title="Delete" color="text-destructive" testid={`delete-${r.id}`}><Trash2 className="w-4 h-4" /></IconBtn>
              </div>
            </div>

            {r.title && <div className="font-semibold text-sm mt-2">{r.title}</div>}
            <p className="text-sm text-foreground/85 whitespace-pre-line mt-1">{r.comment}</p>
            {r.images?.length > 0 && (
              <div className="mt-2 flex gap-2 flex-wrap">
                {r.images.map((img, i) => (
                  <a key={i} href={img} target="_blank" rel="noreferrer" className="w-16 h-16 rounded-lg overflow-hidden border border-border">
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            )}
            {r.admin_reply && (
              <div className="mt-2 pl-3 border-l-2 border-primary bg-primary/5 rounded-r p-2 text-xs">
                <b>Admin Reply:</b> {r.admin_reply}
              </div>
            )}
          </div>
        ))}
      </div>

      {replyOn && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setReplyOn(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-bold">Reply to review</h2>
              <button onClick={() => setReplyOn(null)} className="p-1.5 hover:bg-muted rounded"><X className="w-5 h-5" /></button>
            </div>
            <div className="text-xs bg-muted/40 p-3 rounded mb-3">
              <div className="font-semibold">{replyOn.user_name} · {replyOn.rating}★</div>
              <p className="text-muted-foreground mt-1 line-clamp-3">{replyOn.comment}</p>
            </div>
            <textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={4} maxLength={2000} placeholder="Thanks for the feedback! We'd like to add that…" className="w-full px-3 py-2 border border-border rounded-lg bg-white text-sm" data-testid="reply-textarea" />
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => setReplyOn(null)} className="text-sm font-semibold px-4 py-2 rounded-lg hover:bg-muted">Cancel</button>
              <button onClick={submitReply} className="bg-primary text-white font-bold text-sm px-5 py-2 rounded-lg" data-testid="reply-submit">Post Reply</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const IconBtn = ({ children, onClick, title, color, testid }) => (
  <button onClick={onClick} title={title} className={`p-1.5 rounded hover:bg-muted ${color}`} data-testid={testid}>{children}</button>
);

export default AdminReviews;
